/**
 * The onboarding flow, in plan.md §5's order. Each step names the ONE
 * decision it asks for; that question is the screen's headline. Sign-up
 * happens before the shell exists and is not a step.
 *
 * Removed at the owner's request (2026-09-25, #873): Domains — a site key now
 * works on any website by default (migration 059), and the optional lock
 * lives on Sites & keys — and Plan ("What it costs"). Removed in the
 * 2026-09-27 portal audit: Network fallback, which had nothing for the
 * partner to do (a FinderlyFix operator sets it); it is a read-only row on
 * the Organisation page now, and `/setup/fallback` redirects there.
 *
 * The step counter ("Step 2 of 8") and the rail are both this list filtered
 * by role, so they cannot disagree. Which steps are done is setup/status.ts.
 */
import { can, type Capability } from '../auth/roles';
import type { PartnerRole } from '../api/types';

export interface SetupStep {
  slug: string;
  title: string;
  question: string;
  capability: Capability;
  /** Shown on the rail as "Optional"; never counted in "steps left". */
  optional?: boolean;
  /** For an optional step: why it is optional, shown under its headline. */
  optionalNote?: string;
}

export const SETUP_STEPS: SetupStep[] = [
  { slug: 'trades', title: 'Trades & area', question: 'What will the widget diagnose?', capability: 'editConfig' },
  { slug: 'installer', title: 'Who installs it', question: 'Can you edit your website?', capability: 'members' },
  { slug: 'install', title: 'Install', question: 'Get the snippet onto your page', capability: 'sites' },
  { slug: 'verify', title: 'Verify', question: 'Is it live?', capability: 'sites' },
  // Optional since the second 2026-09-27 audit: the default look works, and a
  // partner who published without changing it was told the step was left.
  {
    slug: 'branding',
    title: 'Branding & copy',
    question: 'Does this look like you?',
    capability: 'editConfig',
    optional: true,
    optionalNote: 'Optional — set colours to match your site. The defaults work if you skip it.',
  },
  { slug: 'routing', title: 'Where leads go', question: 'Who gets pinged?', capability: 'destinations' },
  // "Add contractors", not "Contractors": the Configure section has a
  // Contractors entry for the full roster, and two identical names with two
  // addresses read as a mistake (third live review, 2026-09-27).
  { slug: 'contractors', title: 'Add contractors', question: 'Who does the work?', capability: 'roster', optional: true },
  { slug: 'test', title: 'Test a lead', question: 'Does the value show up?', capability: 'leads' },
];

export function stepBySlug(slug: string): SetupStep | undefined {
  return SETUP_STEPS.find((s) => s.slug === slug);
}

/** The steps this role sees, in order: the rail, the counter and "steps left" all start here. */
export function stepsFor(role: PartnerRole | null): SetupStep[] {
  return SETUP_STEPS.filter((s) => can(role, s.capability));
}

/** "Step 2 of 8" for this role, or null when the role does not see the step. */
export function stepPosition(slug: string, role: PartnerRole | null): { n: number; total: number } | null {
  const visible = stepsFor(role);
  const idx = visible.findIndex((s) => s.slug === slug);
  return idx < 0 ? null : { n: idx + 1, total: visible.length };
}
